import styled from 'styled-components';
import {
  TableView,
  TableHeader,
  TableBody,
  Row,
  Cell,
  Column,
  Text,
} from '@adobe/react-spectrum';
import CheckmarkCircle from '@spectrum-icons/workflow/CheckmarkCircle';
import AlertCircleFilled from '@spectrum-icons/workflow/AlertCircleFilled';
import { useGetLogs as useGetLogsOriginal } from 'feature/logs';
import { useCallback, useMemo, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroller';
import { OpenJsonPageButton } from 'components/parts/openJsonPageButton';
import LinkOut from '@spectrum-icons/workflow/LinkOut';
import { DateCellRenderer } from './common/dateCellRenderer';
import { useWorkflowAppsContext } from 'components/providers/workflowAppsContext';

const LogListLayout = styled.div`
  margin: 10px 150px;
  min-width: 1000px;
`;
const Headline = styled.div`
  font-size: 16px;
  font-weight: bold;
  margin-bottom: 10px;
`;
const LogListTable = styled('div').withConfig({
  shouldForwardProp: (props) => props !== 'isOpen',
})<{ isOpen: boolean }>`
  height: ${(props) => (props.isOpen ? 800 : 60)}px;
  overflow: auto;
`;
const Status = styled.div`
  align-items: center;
  display: flex;
`;
const Icon = styled.div`
  display: flex;
  padding-right: 10px;
`;
const MessageLayout = styled.div`
  margin: 15px 0;
  text-align: center;
`;

const TextLayout = styled.div`
  align-items: center;
  display: flex;
`;

type TableHeaderColumn = {
  id: string;
  name: string;
  width?: number;
};

const logStatus = {
  accepted: '実行前',
  processing: '実行中',
  completed: '正常終了',
  error: 'エラー',
  canceled: 'キャンセル',
};

const statusIcon = (status: string) => {
  switch (status) {
    case 'completed':
      return <CheckmarkCircle color="positive" />;
    case 'error':
      return <AlertCircleFilled color="negative" />;
    default:
      break;
  }
};

interface LogListProps {
  wfId: number;
  useGetLogs?: typeof useGetLogsOriginal;
}

export const LogList = (props: LogListProps) => {
  const { wfId, useGetLogs = useGetLogsOriginal } = props;

  const [getLogs, logs, loading, hasMore, error] = useGetLogs();
  const [page, setPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);
  const workflowAppsContext = useWorkflowAppsContext();

  const columns = [
    { id: 'status', name: '状態', width: 180 },
    { id: 'executeAtUTCKey', name: '実行開始日時', width: 250 },
    { id: 'executeEndAtUTC', name: '実行終了日時', width: 250 },
    { id: 'errorMessage', name: 'エラー内容' },
  ] as TableHeaderColumn[];

  const handleEndReached = useCallback(async () => {
    // wfIdがnumberでない場合、ログ取得時にエラーとなるためはじく
    if (loadingMore || loading || isNaN(wfId)) return;

    setLoadingMore(true);

    // スクロール位置を保存
    let scrollTop = 0;
    const logElement = document.getElementById('log-list-table');
    if (logElement) scrollTop = logElement.scrollTop;

    // ログ取得
    await getLogs(wfId, page, 50);
    setPage(page + 1);
    setLoadingMore(false);

    // 保存したスクロール位置を設定
    if (logElement) logElement.scrollTop = scrollTop;
  }, [loadingMore, loading, getLogs, wfId, page]);

  // アプリ利用可能フラグ
  const enableFlg = useMemo(() => {
    const workflowAppInfo = workflowAppsContext.workflowApps.find(
      (workflowApp) => Number(workflowApp.id) === Number(wfId),
    );
    return !!workflowAppInfo?.enableFlg;
  }, [wfId, workflowAppsContext.workflowApps]);

  return (
    <LogListLayout>
      <Headline>アプリ実行履歴</Headline>
      <LogListTable id={'log-list-table'} isOpen={logs.length > 0 && !error}>
        <InfiniteScroll
          loadMore={handleEndReached}
          hasMore={hasMore}
          initialLoad={logs.length === 0}
          useWindow={false}
          threshold={50}
        >
          {!loading && error && enableFlg && (
            <MessageLayout>
              アプリケーション実行履歴の取得に失敗しました
            </MessageLayout>
          )}
          {!loading && ((logs.length === 0 && !error) || !enableFlg) && (
            <MessageLayout>
              アプリケーションの実行履歴がありません
            </MessageLayout>
          )}
          {logs.length > 0 && !error && enableFlg && (
            <TableView
              data-testid="log-list"
              aria-label="log-list"
              overflowMode="wrap"
            >
              {/* テーブルヘッダー */}
              <TableHeader columns={columns}>
                {(column) => (
                  <Column key={column.id} width={column.width}>
                    {column.name}
                  </Column>
                )}
              </TableHeader>
              {/* テーブルボディ */}
              <TableBody items={logs}>
                {(item) => (
                  <Row key={item.id}>
                    <Cell>
                      <Status>
                        <Icon>{statusIcon(item['status'])}</Icon>
                        <Text>{logStatus[item.status]}</Text>
                      </Status>
                    </Cell>
                    <Cell>
                      <TextLayout>
                        <DateCellRenderer date={item.executeAtUTCKey} />
                        <OpenJsonPageButton margin={10} id={item.id} ioFlg="0">
                          <LinkOut />
                        </OpenJsonPageButton>
                      </TextLayout>
                    </Cell>
                    <Cell>
                      <TextLayout>
                        <DateCellRenderer date={item.executeEndAtUTC} />
                        <OpenJsonPageButton margin={10} id={item.id} ioFlg="1">
                          <LinkOut />
                        </OpenJsonPageButton>
                      </TextLayout>
                    </Cell>
                    <Cell>
                      <Text>
                        {item?.errorMessage
                          ? item?.errorMessage
                          : item?.message}
                      </Text>
                    </Cell>
                  </Row>
                )}
              </TableBody>
            </TableView>
          )}
        </InfiniteScroll>
      </LogListTable>
    </LogListLayout>
  );
};
